import { EmployerOwnership, EmployerTypeAnswer, PaidByPartnerHospital, ResultStage } from 'api/StudentLoanApi';
import { StudentLoanDataState } from 'handlers/studentLoanData';
import { RootState } from 'handlers';

export const getStudentLoanData = ({ studentLoanData }: RootState) => studentLoanData;

export const getStudentLoanPlanResult = ({ result }: StudentLoanDataState) =>
  result?.stage === ResultStage.Estimate || result?.stage === ResultStage.Plan ? result : undefined;

export const getStudentLoanBalance = (data: StudentLoanDataState): number | undefined =>
  getStudentLoanPlanResult(data)?.balance;

/** Ownership of the borrower's employer: the hospital record when it pays them, otherwise what they told us. */
export const getEmployerOwnership = ({ answers, hospital }: StudentLoanDataState): EmployerOwnership | undefined => {
  if (answers.paidByPartnerHospital === PaidByPartnerHospital.Yes) {
    return hospital?.ownership ?? EmployerOwnership.Unknown;
  }
  switch (answers.employerType) {
    case EmployerTypeAnswer.Nonprofit:
      return EmployerOwnership.Nonprofit;
    case EmployerTypeAnswer.Government:
      return EmployerOwnership.Government;
    case EmployerTypeAnswer.ForProfit:
      return EmployerOwnership.ForProfit;
    case EmployerTypeAnswer.NotSure:
      return EmployerOwnership.Unknown;
    default:
      return undefined;
  }
};

export const isEmployerForProfit = (data: StudentLoanDataState) =>
  getEmployerOwnership(data) === EmployerOwnership.ForProfit;

/** A borrower whose employer might count toward PSLF (qualifying type, or one we cannot rule out). */
export const isEmployerPossiblyPslfQualifying = (data: StudentLoanDataState) => !isEmployerForProfit(data);

/** True when the calculator found federal loans and a plan that saves the borrower money. */
export const hasStudentLoanSavings = (data: StudentLoanDataState) => {
  const planResult = getStudentLoanPlanResult(data);
  return Boolean(planResult?.savings && planResult.savings.high > 0);
};
